import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AdminProviderModelHealthRow, AdminProviderHealthWindow } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { WINDOWS, successTone, fmtLatency, topError } from './health-format';

/**
 * Per-model (entity) reliability within one provider — answers "WHICH model is
 * failing?" when the provider-level rate degrades. Rows are the provider's
 * entities with attributed traffic in the window, highest volume first.
 */
export function ProviderModelHealthPanel({ provider }: { provider: string }) {
  const [windowKey, setWindowKey] = useState<AdminProviderHealthWindow>('24h');
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.adminProviders.modelHealth(provider, windowKey),
    queryFn: () => api.adminGetProviderModelHealth(provider, windowKey),
  });

  const models: AdminProviderModelHealthRow[] = data?.models ?? [];

  return (
    <div className="rounded-lg border border-border bg-card p-4 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold">Per-model reliability</h2>
        <div className="flex gap-1 text-xs">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => setWindowKey(w.key)}
              className={`px-2 py-1 rounded ${windowKey === w.key ? 'bg-primary text-primary-foreground' : 'border border-border'}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Success rate and latency broken down by entity (model) for this provider's attributed
        invocations.
      </p>

      {isError ? (
        <p className="text-sm text-destructive">Failed to load per-model health.</p>
      ) : isLoading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : models.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No attributed invocations for this provider in this window.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-1.5 pr-4 font-medium">Model / entity</th>
                <th className="py-1.5 pr-4 font-medium text-right">Calls</th>
                <th className="py-1.5 pr-4 font-medium text-right">Success</th>
                <th className="py-1.5 pr-4 font-medium text-right">p50</th>
                <th className="py-1.5 pr-4 font-medium text-right">p95</th>
                <th className="py-1.5 font-medium text-right">Top error</th>
              </tr>
            </thead>
            <tbody>
              {models.map((m) => (
                <tr key={m.entityId} className="border-b border-border last:border-0">
                  <td className="py-1.5 pr-4">
                    <span title={m.entityId}>{m.entityName}</span>
                  </td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">
                    {m.totalInvocations.toLocaleString()}
                  </td>
                  <td
                    className={`py-1.5 pr-4 text-right tabular-nums ${successTone(m.successRate)}`}
                  >
                    {(m.successRate * 100).toFixed(1)}%
                  </td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">
                    {fmtLatency(m.p50LatencyMs)}
                  </td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">
                    {fmtLatency(m.p95LatencyMs)}
                  </td>
                  <td className="py-1.5 text-right">{topError(m.errorCounts)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
